import colors from '@mathflat/common/style/colors'
import { observer } from 'mobx-react'
import type { ChangeEvent, CSSProperties } from 'react'
import * as React from 'react'
import { useEffect, useImperativeHandle, useMemo, useRef } from 'react'

import { Icon } from '@/component/atoms/Icon'
import { domUtils } from '@/utils/domUtils'

import S from './Input.style'

type NumberTypeProps = {
  isShowNumberController?: boolean
}

export type Props = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  height?: CSSProperties['height']
} & NumberTypeProps

// 글로벌 이벤트 트리거
const globalInputChangeTrigger = (el: HTMLInputElement, newValue: string) => {
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(el, newValue)
  el.dispatchEvent(new Event('change', { bubbles: true }))
}

const Input = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      height = '40px',
      style,
      className,
      type,
      onEnter,
      onKeyPress,
      onChange,
      onBlur,
      isShowNumberController = type === 'number',
      min = 0,
      max,
      ...props
    }: Props,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    // forwardRef와 ref 공유
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [ref])

    // type === number 일때 사용되는 이전 값, 핸들러
    const beforeNumberValue = useRef(props.defaultValue ?? props.value ?? '')

    const onNumberChange = useMemo(
      () => (val: string) => {
        if (!inputRef.current) return
        if (/^-?[0-9]*$/.test(val)) {
          if (+val < +min) {
            inputRef.current.value = `${min}`
          } else if (max && +val > +max) {
            inputRef.current.value = `${max}`
          } else {
            beforeNumberValue.current = inputRef.current.value
            inputRef.current.value = val
          }
        } else {
          const beforeCaretEnd = inputRef.current.selectionEnd
          // regexp에 맞지 않으면 이전 값으로 되돌림
          inputRef.current.value = String(beforeNumberValue.current)
          // 커서 움직이지 않게
          if (beforeCaretEnd !== null) inputRef.current.selectionEnd = beforeCaretEnd - 1
        }
        if (/^(-?)(0+)([1-9]\d*)/.test(inputRef.current.value)) {
          inputRef.current.value = String(beforeNumberValue.current).replace(
            /^(-?)(0+)([1-9]\d*)/,
            (_, $1, $2, $3) => `${$1}${$3}`,
          )
        }
      },
      [],
    )
    const handleNumberChange = useMemo(
      () => (e: ChangeEvent<HTMLInputElement>) => {
        onNumberChange(e.target.value)
        onChange?.(e)
      },
      [onChange],
    )
    const handleBlur = useMemo(
      () => (e: React.FocusEvent<HTMLInputElement>) => {
        if (onBlur) {
          onBlur(e)
          return
        }

        if (!inputRef.current) return

        if (inputRef.current.value === '' || inputRef.current.value === '-') {
          globalInputChangeTrigger(inputRef.current, '0')
        }
      },
      [onBlur],
    )

    // type === password 일때 사용되는 것들
    const [isPasswordVisible, setPasswordVisible] = React.useState(false)
    const togglePasswordVisible = () => {
      setPasswordVisible(!isPasswordVisible)
    }

    // 타입에 따른 onChange 핸들러 분기처리
    const handleChange = useMemo(() => {
      switch (type) {
        case 'number':
          return handleNumberChange
        default:
          return onChange
      }
    }, [type, onChange])

    // 특정 액션 키 세팅
    const handleKeyDown = useMemo(
      () => (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
          case 'ArrowUp':
          case 'ArrowDown':
            if (type !== 'number') break
            e.key === 'ArrowUp' ? inc() : dec()
            e.preventDefault()
            break
          case 'Enter':
            onEnter && domUtils.onEnter(onEnter)(e)
            break
        }
        onKeyPress?.(e)

        e.stopPropagation()
      },
      [onKeyPress],
    )

    const { inc, dec } = useMemo(() => {
      const changeNum = (n: number) => {
        if (inputRef.current) {
          const newValue = Number(inputRef.current.value) + n
          globalInputChangeTrigger(inputRef.current, `${newValue}`)
        }
      }
      return {
        inc: () => {
          if (max && inputRef.current && +inputRef.current.value >= +max) return
          changeNum(1)
        },
        dec: () => {
          if (inputRef.current && +inputRef.current.value <= +min) return
          changeNum(-1)
        },
      }
    }, [min, max])

    // 계산된 후 Real input 타입
    const inputType = useMemo(() => {
      switch (type) {
        case 'password':
          return isPasswordVisible ? 'text' : 'password'
        case 'number':
          return 'text'
        default:
          return type
      }
    }, [type, isPasswordVisible])

    // input이 아닌 input을 감싸고 있는 div를 이용해서 화면 사라짐, 나타남을 표시.
    // window 에서 발생하는 composition 관련 스크롤 이슈를 수정하기 위해서 input을 계속 화면 안에 위치하도록 고정
    const divRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (!divRef.current) return

      const intersectionObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) {
          if (divRef.current) {
            divRef.current.style.height = '40px'
            ;(divRef.current.firstChild as HTMLInputElement).style.position = 'fixed'
            ;(divRef.current.firstChild as HTMLInputElement).style.opacity = '0'
            ;(divRef.current.firstChild as HTMLInputElement).style.zIndex = '-1'
          }
        } else {
          if (divRef.current) {
            divRef.current.style.height = ''
            ;(divRef.current.firstChild as HTMLInputElement).style.position = ''
            ;(divRef.current.firstChild as HTMLInputElement).style.opacity = '1'
            ;(divRef.current.firstChild as HTMLInputElement).style.zIndex = ''
          }
        }
      })
      intersectionObserver.observe(divRef.current)

      return () => intersectionObserver.disconnect()
    }, [])

    return (
      <div css={S.wrapper} style={style} className={className} ref={divRef}>
        <input
          type={inputType}
          css={[S.input, { height }]}
          ref={inputRef}
          inputMode={type === 'number' ? 'numeric' : undefined}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onBlur={type === 'number' ? handleBlur : onBlur}
          {...props}
        />
        {type === 'password' && (
          <button css={S.eyeButton} onClick={togglePasswordVisible}>
            {isPasswordVisible ? (
              <Icon
                name="eye_opened"
                data-test-id="eye_opened"
                size={20}
                color={colors.gray.$900}
              />
            ) : (
              <Icon
                name="eye_closed"
                data-test-id="eye_closed"
                size={20}
                color={colors.gray.$500}
              />
            )}
          </button>
        )}
        {isShowNumberController && (
          <div className="number-buttons-wrap">
            <button className="input-number-inc" onClick={inc}>
              <Icon name="dropdown_arrow_up" size={14} />
            </button>
            <button className="input-number-dec" onClick={dec}>
              <Icon name="dropdown_arrow_down" size={14} />
            </button>
          </div>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'

export default observer(Input)
